import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { useTenant } from "@/lib/TenantContext";
import {
  ArrowLeft,
  Printer,
  Search,
  Copy,
  Check,
  ShieldCheck,
  Scale,
  FileText,
  Sparkles,
  AlertTriangle,
  HeartHandshake,
  CreditCard,
  Mail,
  Globe,
  Building,
  X,
  Lock,
} from "lucide-react";
import legalFr from "../i18n/legal/fr";
import legalEn from "../i18n/legal/en";
import legalEs from "../i18n/legal/es";
import SupportTicketModal from "../components/support/SupportTicketModal";

const LEGAL_DOCS = { fr: legalFr, en: legalEn, es: legalEs };

export default function TermsAndConditions() {
  const { lang, setLang } = useLanguage();
  const { tenant } = useTenant();
  const location = useLocation();
  const t = useT("saas");
  const tCommon = useT("common");

  const [searchQuery, setSearchQuery] = useState("");
  const [activeSectionId, setActiveSectionId] = useState("");
  const [copiedSectionId, setCopiedSectionId] = useState(null);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const observerRef = useRef(null);

  // Normalize language to fr, en, or es (fallback to en)
  const currentLang = lang === "fr" || lang === "en" || lang === "es" ? lang : "en";
  const docData = (LEGAL_DOCS[currentLang] || LEGAL_DOCS.en).terms;

  const pageTitle = t("terms.pageTitle") || "Terms and Conditions";
  const pageSubtitle = t("terms.pageSubtitle") || "Everything you need to know about your rights, responsibilities, and use of the Solunary platform.";

  useEffect(() => {
    document.title = `${pageTitle} — Solunary`;
  }, [pageTitle]);

  // Read section hash from URL on initial mount
  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.replace("#", "");
      setActiveSectionId(targetId);
      const element = document.getElementById(targetId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location.hash]);

  // Set up intersection observer for scroll spy
  useEffect(() => {
    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSectionId(entry.target.id);
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: "-20% 0px -70% 0px",
      threshold: 0,
    });

    const sections = document.querySelectorAll("article[id]");
    sections.forEach((sec) => observerRef.current.observe(sec));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [docData.sections, searchQuery]);

  // Filter sections based on real-time search query
  const filteredSections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return docData.sections;

    return docData.sections.filter((sec) => {
      const inHeading = sec.heading.toLowerCase().includes(query);
      const inCategory = sec.category && sec.category.toLowerCase().includes(query);
      const inParagraphs = sec.paragraphs.some((p) => p.toLowerCase().includes(query));
      const inCallout =
        sec.callout &&
        (sec.callout.title.toLowerCase().includes(query) || sec.callout.body.toLowerCase().includes(query));
      return inHeading || inCategory || inParagraphs || inCallout;
    });
  }, [docData.sections, searchQuery]);

  const handleCopyLink = (sectionId) => {
    const url = `${window.location.origin}${window.location.pathname}#${sectionId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedSectionId(sectionId);
      setTimeout(() => setCopiedSectionId(null), 2500);
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Practice specific booking terms (if viewed in the context of a tenant with custom settings)
  // The reader's language first, then English when the practice wrote none in it.
  const bookingTermsIn = (code) =>
    tenant?.[`booking_terms_${code}`] || tenant?.settings?.[`booking_terms_${code}`];
  const practiceBookingTerms = bookingTermsIn(currentLang) || bookingTermsIn("en");

  return (
    <div
      className="bg-sol-ivory min-h-screen text-sol-midnight font-sans selection:bg-sol-gold/30 selection:text-sol-midnight"
      data-testid="terms-and-conditions-page"
    >
      {/* Hidden landmark for backward compatibility */}
      <span data-testid="legal-terms" className="hidden" aria-hidden="true" />

      {/* Top Header Navigation */}
      <header className="sticky top-0 z-40 bg-sol-midnight text-sol-ivory border-b border-sol-ivory/10 shadow-sm print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/platform" className="flex items-center gap-2.5 group">
              <img
                src="/brand/solunary-logo.png"
                alt="Solunary"
                className="h-9 w-9 object-contain group-hover:scale-105 transition-transform"
              />
              <span className="font-sol font-semibold text-lg text-sol-ivory tracking-tight">Solunary</span>
            </Link>
            <div className="hidden sm:flex items-center gap-2 text-xs text-sol-ivory/60 border-l border-sol-ivory/15 pl-4">
              <Scale className="w-3.5 h-3.5 text-sol-gold" />
              <span>Legal Center</span>
            </div>
          </div>

          <div className="flex items-center gap-2.5 sm:gap-4">
            {/* Language Switcher */}
            <div className="flex items-center bg-sol-ivory/10 rounded-lg p-0.5" data-testid="terms-lang-switcher">
              <button
                onClick={() => setLang("en")}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition ${
                  currentLang === "en"
                    ? "bg-sol-gold text-sol-midnight shadow-sm font-semibold"
                    : "text-sol-ivory/70 hover:text-sol-ivory"
                }`}
                title="English"
              >
                EN
              </button>
              <button
                onClick={() => setLang("fr")}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition ${
                  currentLang === "fr"
                    ? "bg-sol-gold text-sol-midnight shadow-sm font-semibold"
                    : "text-sol-ivory/70 hover:text-sol-ivory"
                }`}
                title="Français" // i18n-ignore
              >
                FR
              </button>
              <button
                onClick={() => setLang("es")}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition ${
                  currentLang === "es"
                    ? "bg-sol-gold text-sol-midnight shadow-sm font-semibold"
                    : "text-sol-ivory/70 hover:text-sol-ivory"
                }`}
                title="Español"
              >
                ES
              </button>
            </div>

            {/* Print Button */}
            <button
              onClick={handlePrint}
              data-testid="terms-print-button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-sol-ivory/10 hover:bg-sol-ivory/20 text-sol-ivory transition"
              title={t("terms.print") || "Print or Save as PDF"}
            >
              <Printer className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t("terms.print") || "Print"}</span>
            </button>

            {/* Back to Home Link */}
            <Link
              to="/platform"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-sol-ivory/80 hover:text-sol-gold transition pl-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>{tCommon("actions.back") || "Back"}</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-sol-midnight to-[#131d2e] text-sol-ivory pt-12 pb-14 px-4 sm:px-6 lg:px-8 print:py-4 print:bg-white print:text-black">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sol-gold/15 border border-sol-gold/30 text-sol-gold text-xs font-medium mb-4 print:hidden">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Official Policy · Ver. {docData.version}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-sol-ivory print:text-black mb-4">
              {pageTitle}
            </h1>
            <p className="text-sol-ivory/70 text-base sm:text-lg leading-relaxed print:text-gray-700 max-w-2xl mb-6">
              {pageSubtitle}
            </p>

            {/* Metadata badges */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-sol-ivory/60 print:text-gray-600">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                <span>
                  {t("terms.lastUpdated") || "Last updated"}: <strong className="text-sol-ivory font-medium print:text-black">{docData.lastUpdatedDate}</strong>
                </span>
              </div>
              <span className="hidden sm:inline text-sol-ivory/25">·</span>
              <div>
                <span>
                  {t("terms.effectiveDate") || "Effective"}: <strong className="text-sol-ivory font-medium print:text-black">{docData.effectiveDate}</strong>
                </span>
              </div>
              <span className="hidden sm:inline text-sol-ivory/25">·</span>
              <div className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-sol-gold" />
                <span>Multi-Tenant Architecture</span>
              </div>
            </div>
          </div>

          {/* Search bar */}
          <div className="mt-8 max-w-xl relative print:hidden">
            <Search className="w-4 h-4 text-sol-ivory/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("terms.searchPlaceholder") || "Search terms, clauses, topics..."}
              data-testid="terms-search-input"
              className="w-full pl-10 pr-10 py-2.5 bg-sol-ivory/10 hover:bg-sol-ivory/15 focus:bg-sol-ivory/20 border border-sol-ivory/20 rounded-xl text-sol-ivory placeholder-sol-ivory/40 text-sm focus:outline-none focus:ring-2 focus:ring-sol-gold/50 transition"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sol-ivory/40 hover:text-sol-ivory p-1"
                title={t("terms.clearSearch") || "Clear search"}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {/* Executive Highlights Summary */}
        <section className="mb-12 print:hidden" aria-label="Key Highlights">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-sol-gold" />
            <h2 className="text-xs uppercase tracking-wider font-semibold text-sol-midnight/60">
              {t("terms.summaryTitle") || "Key Highlights at a Glance"}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Merchant of Record */}
            <div className="bg-white rounded-xl p-5 border border-sol-midnight/8 shadow-sm hover:shadow-md transition">
              <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center mb-3">
                <CreditCard className="w-4 h-4" />
              </div>
              <h3 className="font-sol font-semibold text-sm text-sol-midnight mb-1.5">
                {t("terms.summary1Title") || "Merchant of Record"}
              </h3>
              <p className="text-xs text-sol-midnight/70 leading-relaxed">
                {t("terms.summary1Desc") || "Solunary collects client payments and maintains double-entry ledger settlement via Stripe."}
              </p>
            </div>

            {/* Card 2: Wellness Records */}
            <div className="bg-white rounded-xl p-5 border border-sol-midnight/8 shadow-sm hover:shadow-md transition">
              <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-700 flex items-center justify-center mb-3">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <h3 className="font-sol font-semibold text-sm text-sol-midnight mb-1.5">
                {t("terms.summary2Title") || "Wellness Records"}
              </h3>
              <p className="text-xs text-sol-midnight/70 leading-relaxed">
                {t("terms.summary2Desc") || "Solunary is not a certified EMR. Practitioners retain clinical and regulatory responsibility."}
              </p>
            </div>

            {/* Card 3: AI Human-in-the-Loop */}
            <div className="bg-white rounded-xl p-5 border border-sol-midnight/8 shadow-sm hover:shadow-md transition">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center mb-3">
                <Sparkles className="w-4 h-4" />
              </div>
              <h3 className="font-sol font-semibold text-sm text-sol-midnight mb-1.5">
                {t("terms.summary3Title") || "Human-in-the-Loop AI"}
              </h3>
              <p className="text-xs text-sol-midnight/70 leading-relaxed">
                {t("terms.summary3Desc") || "All AI outputs are assistive drafts requiring practitioner review before client delivery."}
              </p>
            </div>

            {/* Card 4: Data Ownership */}
            <div className="bg-white rounded-xl p-5 border border-sol-midnight/8 shadow-sm hover:shadow-md transition">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-3">
                <Lock className="w-4 h-4" />
              </div>
              <h3 className="font-sol font-semibold text-sm text-sol-midnight mb-1.5">
                {t("terms.summary4Title") || "Your Data, Your Content"}
              </h3>
              <p className="text-xs text-sol-midnight/70 leading-relaxed">
                {t("terms.summary4Desc") || "You retain 100% intellectual property ownership of your clients, courses, and practice data."}
              </p>
            </div>
          </div>
        </section>

        {/* Practice-specific booking terms if in tenant mode */}
        {practiceBookingTerms && (
          <div
            className="mb-12 bg-white rounded-2xl p-6 sm:p-8 border border-sol-gold/40 shadow-sm"
            data-testid="terms-practice-booking-terms"
          >
            <div className="flex items-center gap-2 mb-3">
              <Building className="w-4 h-4 text-sol-gold" />
              <span className="text-xs font-bold uppercase tracking-wider text-sol-gold px-2.5 py-0.5 rounded-full bg-sol-gold/15">
                {tenant?.name || t("terms.practiceTermsBadge") || "Practice"}
              </span>
            </div>
            <h2 className="font-sol font-semibold text-xl text-sol-midnight mb-3">
              {t("terms.practiceTermsTitle") || "Practice-Specific Booking Terms"}
            </h2>
            <div className="p-4 bg-sol-ivory/60 rounded-xl border border-sol-midnight/5 text-sm text-sol-midnight/85 whitespace-pre-line leading-relaxed font-sans">
              {practiceBookingTerms}
            </div>
          </div>
        )}

        {/* Main Grid: Sidebar TOC + Articles */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Sticky Sidebar (Table of Contents) */}
          <aside className="lg:col-span-4 sticky top-20 hidden lg:block print:hidden" data-testid="terms-toc">
            <div className="bg-white rounded-2xl p-6 border border-sol-midnight/8 shadow-sm max-h-[calc(100vh-6rem)] overflow-y-auto">
              <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-sol-midnight/8">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-sol-gold" />
                  <span className="font-sol font-semibold text-xs tracking-wider uppercase text-sol-midnight">
                    {t("terms.tableOfContents") || "Table of Contents"}
                  </span>
                </div>
                <span className="text-xs text-sol-midnight/40 font-mono">
                  {filteredSections.length}
                </span>
              </div>

              {searchQuery && (
                <div className="mb-3 text-xs text-sol-midnight/60 px-1">
                  {t("terms.showingCount", { count: filteredSections.length }) ||
                    `${filteredSections.length} sections found`}
                </div>
              )}

              <nav className="space-y-1">
                {filteredSections.map((sec) => {
                  const isActive = activeSectionId === sec.id;
                  return (
                    <a
                      key={sec.id}
                      href={`#${sec.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveSectionId(sec.id);
                        const el = document.getElementById(sec.id);
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }}
                      className={`group flex items-start gap-2.5 px-3 py-2 rounded-lg text-xs transition leading-snug ${
                        isActive
                          ? "bg-sol-midnight text-sol-ivory font-semibold shadow-sm"
                          : "text-sol-midnight/70 hover:bg-sol-ivory hover:text-sol-midnight"
                      }`}
                    >
                      <span
                        className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                          isActive ? "bg-sol-gold" : "bg-sol-midnight/20 group-hover:bg-sol-gold"
                        }`}
                      />
                      <span className="truncate">{sec.heading}</span>
                    </a>
                  );
                })}
              </nav>

              {/* Bottom sidebar utility */}
              <div className="mt-6 pt-4 border-t border-sol-midnight/8 text-xs text-sol-midnight/50 space-y-2">
                <div className="flex items-center justify-between">
                  <span>Privacy Policy:</span>
                  <Link to="/legal/privacy" className="text-sol-gold hover:underline font-medium">
                    /legal/privacy
                  </Link>
                </div>
                <div className="flex items-center justify-between">
                  <span>Questions:</span>
                  <a href="mailto:hey@solunary.health" className="text-sol-midnight/80 hover:text-sol-gold font-medium">
                    hey@solunary.health
                  </a>
                </div>
              </div>
            </div>
          </aside>

          {/* Right Column: Section Articles */}
          <main className="lg:col-span-8 space-y-10">
            {filteredSections.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-sol-midnight/8 shadow-sm">
                <Search className="w-10 h-10 text-sol-midnight/20 mx-auto mb-3" />
                <h3 className="font-sol font-semibold text-lg text-sol-midnight mb-2">
                  {t("terms.noResults") || "No clauses match your search query."}
                </h3>
                <p className="text-sm text-sol-midnight/60 mb-6">
                  Try searching for keywords like &ldquo;merchant&rdquo;, &ldquo;medical&rdquo;, &ldquo;refund&rdquo;, or &ldquo;AI&rdquo;.
                </p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="px-4 py-2 bg-sol-midnight text-sol-ivory text-xs font-semibold rounded-lg hover:bg-sol-midnight/90 transition"
                >
                  {t("terms.clearSearch") || "Clear search"}
                </button>
              </div>
            ) : (
              filteredSections.map((section) => (
                <article
                  key={section.id}
                  id={section.id}
                  data-testid={`terms-section-${section.id}`}
                  className="bg-white rounded-2xl p-6 sm:p-8 border border-sol-midnight/8 shadow-sm scroll-mt-24 transition hover:border-sol-midnight/15 print:border-none print:shadow-none print:p-0 print:mb-6"
                >
                  {/* Section Header with Category & Copy Link */}
                  <div className="flex items-start justify-between gap-4 mb-4 pb-3 border-b border-sol-midnight/6">
                    <div>
                      {section.category && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-sol-midnight/50 bg-sol-ivory px-2 py-0.5 rounded border border-sol-midnight/10 mb-2 inline-block">
                          {section.category}
                        </span>
                      )}
                      <h2 className="font-sol font-semibold text-xl sm:text-2xl text-sol-midnight tracking-tight">
                        {section.heading}
                      </h2>
                    </div>

                    <button
                      onClick={() => handleCopyLink(section.id)}
                      className="inline-flex items-center gap-1 p-1.5 rounded-lg text-xs text-sol-midnight/40 hover:text-sol-midnight hover:bg-sol-ivory transition print:hidden shrink-0"
                      title={t("terms.copyLink") || "Copy link to section"}
                      aria-label={`Copy link to ${section.heading}`}
                    >
                      {copiedSectionId === section.id ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                      {copiedSectionId === section.id && (
                        <span className="text-[11px] font-medium text-emerald-600 hidden sm:inline">
                          {t("terms.copied") || "Copied!"}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Optional Callout Alert for Critical Disclosures */}
                  {section.callout && (
                    <div
                      className={`mb-5 p-4 rounded-xl border text-xs sm:text-sm flex items-start gap-3 ${
                        section.callout.type === "warning"
                          ? "bg-rose-50 border-rose-200 text-rose-900"
                          : "bg-amber-50 border-amber-200 text-amber-900"
                      }`}
                    >
                      {section.callout.type === "warning" ? (
                        <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                      ) : (
                        <Sparkles className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      )}
                      <div>
                        <strong className="block font-semibold mb-1">{section.callout.title}</strong>
                        <p className="leading-relaxed opacity-90">{section.callout.body}</p>
                      </div>
                    </div>
                  )}

                  {/* Paragraphs */}
                  <div className="space-y-4 text-sol-midnight/80 leading-relaxed text-sm sm:text-base font-normal">
                    {section.paragraphs.map((para, i) => (
                      <p key={i} className="leading-relaxed">
                        {para}
                      </p>
                    ))}
                  </div>
                </article>
              ))
            )}

            {/* Bottom Contact & Legal Inquiries Card */}
            <div className="bg-gradient-to-br from-sol-midnight to-[#131d2e] rounded-2xl p-6 sm:p-8 text-sol-ivory print:hidden">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 text-sol-gold text-xs font-semibold uppercase tracking-wider mb-2">
                    <HeartHandshake className="w-4 h-4" />
                    <span>Legal Support</span>
                  </div>
                  <h3 className="font-sol font-semibold text-xl text-sol-ivory mb-2">
                    {t("terms.contactTitle") || "Questions regarding our Terms?"}
                  </h3>
                  <p className="text-sol-ivory/70 text-sm max-w-md">
                    {t("terms.contactDesc") ||
                      "Our legal and compliance team is available to assist practitioners and clients."}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={() => setSupportModalOpen(true)}
                    data-testid="terms-raise-ticket-btn"
                    className="px-5 py-2.5 bg-sol-gold hover:bg-sol-gold/90 text-sol-midnight font-semibold rounded-xl text-xs transition inline-flex items-center justify-center gap-2 shadow-sm"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{t("terms.contactButton") || "Raise Support Ticket"}</span>
                  </button>
                  <a
                    href="mailto:hey@solunary.health"
                    className="px-4 py-2.5 bg-white/10 hover:bg-white/15 text-sol-ivory rounded-xl text-xs font-medium transition inline-flex items-center justify-center"
                  >
                    hey@solunary.health
                  </a>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-sol-midnight/10 bg-white py-8 px-4 sm:px-6 lg:px-8 mt-16 print:hidden">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-sol-midnight/60">
          <div className="flex items-center gap-4">
            <span className="font-medium text-sol-midnight">Solunary Platform</span>
            <span>·</span>
            <span>© {new Date().getFullYear()} Solunary. {t("footer.rights") || "All rights reserved."}</span>
          </div>

          <div className="flex items-center gap-6">
            <Link to="/legal/privacy" className="hover:text-sol-gold transition">
              {t("footer.privacy") || "Privacy Policy"}
            </Link>
            <Link to="/contact" className="hover:text-sol-gold transition">
              Support & Contact
            </Link>
            <Link to="/platform" className="hover:text-sol-gold transition">
              Solunary Home
            </Link>
          </div>
        </div>
      </footer>

      {/* Support Ticket Modal */}
      <SupportTicketModal
        isOpen={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        defaultCategory="legal"
        defaultSubject="Legal inquiry regarding Terms and Conditions"
      />
    </div>
  );
}
